/**
 * A clip's sound effect lane on its card (Dax, 2026-09-29): the effect's waveform seen through the
 * clip's own window, dragged left or right to put its attack where the clip needs it. The whole sample
 * plays, so whatever slides out of the window still sounds under the clips either side; it is simply
 * not drawn. Drawn on canvas like every waveform, so nothing needs a computed inline position.
 *
 * Trims (2026-10-03): the stretch that plays is drawn in colour and the trimmed-off rest faint. Where the
 * caller allows it (the desk's full-length lanes), a press selects the lane: its heard stretch is shaded
 * and handles at its two ends drag the trim, in the file's own milliseconds.
 */
import { useCallback, useEffect, useRef } from 'react';
import { SFX_NUDGE_MAX_MS, TRIM_MIN_MS } from '@studio/contracts';
import { clampSoundOffset } from '@/lib/mix-plan';
import type { ClipColours } from '@/lib/clip-hues';
import type { TrimEdge } from './cut-strip';
import { displayGain } from './waveform';

/** Arrow keys nudge by this much; with Shift, by ten times as much. */
const KEY_STEP_MS = 10;
const KEY_STEP_SHIFT = 10;
/** How close (px) a press must land to a handle to take it rather than the sound. */
const HANDLE_HIT_PX = 6;
const HANDLE_W = 2;

function cssVar(el: HTMLElement, name: string, fallback: string): string {
  return getComputedStyle(el).getPropertyValue(name).trim() || fallback;
}

export function SoundLane({ peaks, durationMs, windowMs, offsetMs, muted, limitMs = SFX_NUDGE_MAX_MS, heard, selected = false, colours, onSelect, onTrim, onNudge }: {
  /** The sample's display waveform, spread evenly over its whole length. */
  peaks: number[];
  durationMs: number;
  /** The clip's length: the window the lane shows. */
  windowMs: number;
  /** Where the sample starts against the clip's first frame. */
  offsetMs: number;
  muted: boolean;
  /** How far it may be pushed either way; a clip's effect by the nudge limit, a full-length file by its length. */
  limitMs?: number;
  /** The stretch of the file that plays (its trim); the whole file when absent. */
  heard?: { fromMs: number; toMs: number };
  /** Shaded, with handles when trimming is allowed. */
  selected?: boolean;
  /** Its clip's colours (clip-hues.ts): a pale ground and the waveform in the clip's hue, as on the desk. */
  colours?: ClipColours;
  onSelect?: () => void;
  /** A handle dragged to `ms` of the file; `done` on release. */
  onTrim?: (edge: TrimEdge, ms: number, done: boolean) => void;
  /** `done` is the release: the value to keep. */
  onNudge: (offsetMs: number, done: boolean) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{ x: number; from: number; edge: TrimEdge | null } | null>(null);
  /** Where the pointer was last seen while dragging: where a drag whose release was lost ends. */
  const lastX = useRef(0);
  const heardFrom = heard?.fromMs ?? 0;
  const heardTo = heard?.toMs ?? durationMs;
  const keyRef = useRef<number | null>(null);

  /** Always some of the sound inside its own clip, so it can be grabbed again. */
  const clamp = useCallback((ms: number) => clampSoundOffset(ms, durationMs, windowMs, limitMs), [durationMs, windowMs, limitMs]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx || !w || !h) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    const bar = muted ? cssVar(canvas, '--wf-bar', '#d5d5da') : colours?.wave ?? cssVar(canvas, '--wf-sfx', '#7c5cff');
    if (colours && !selected) {
      ctx.fillStyle = colours.bg;
      ctx.fillRect(0, 0, w, h);
    }
    const ghost = cssVar(canvas, '--wf-ghost', 'rgba(0,0,0,0.13)');
    const edge = cssVar(canvas, '--wf-cut', 'rgba(0,0,0,0.35)');
    const xAt = (fileMs: number) => (Math.max(0, Math.min(windowMs, fileMs + offsetMs)) / windowMs) * w;
    if (selected) {
      ctx.fillStyle = cssVar(canvas, '--wf-shade', 'rgba(10,111,224,0.1)');
      ctx.fillRect(xAt(heardFrom), 0, xAt(heardTo) - xAt(heardFrom), h);
    }
    const barW = 3;
    const gap = 1;
    const bars = Math.floor(w / (barW + gap));
    const gain = displayGain(peaks, 0, peaks.length);
    for (let b = 0; b < bars; b++) {
      // Window time to sample time: the sample starts `offsetMs` into the window.
      const s0 = (b / bars) * windowMs - offsetMs;
      const s1 = ((b + 1) / bars) * windowMs - offsetMs;
      if (s1 <= 0 || s0 >= durationMs) continue;
      const i0 = Math.max(0, Math.floor((s0 / durationMs) * peaks.length));
      const i1 = Math.min(peaks.length, Math.max(i0 + 1, Math.ceil((s1 / durationMs) * peaks.length)));
      let peak = 0;
      for (let i = i0; i < i1; i++) peak = Math.max(peak, peaks[i] ?? 0);
      const bh = Math.max(2, Math.min(1, peak * gain) * (h - 2));
      ctx.fillStyle = s1 > heardFrom && s0 < heardTo ? bar : ghost;
      ctx.fillRect(b * (barW + gap), (h - bh) / 2, barW, bh);
    }
    // Where the sample begins and ends, when either is inside the window.
    ctx.fillStyle = edge;
    for (const at of [offsetMs, offsetMs + durationMs]) {
      if (at > 0 && at < windowMs) ctx.fillRect((at / windowMs) * w, 0, 1, h);
    }
    if (selected && onTrim) {
      ctx.fillStyle = cssVar(canvas, '--wf-handle', '#0a6fe0');
      for (const ms of [heardFrom, heardTo]) {
        const x = Math.max(0, Math.min(w - HANDLE_W, xAt(ms) - HANDLE_W / 2));
        ctx.fillRect(x, 0, HANDLE_W, h);
        ctx.fillRect(x - HANDLE_W, 0, HANDLE_W * 3, HANDLE_W * 3);
        ctx.fillRect(x - HANDLE_W, h - HANDLE_W * 3, HANDLE_W * 3, HANDLE_W * 3);
      }
    }
  }, [peaks, durationMs, windowMs, offsetMs, muted, selected, onTrim, heardFrom, heardTo, colours]);

  useEffect(() => {
    draw();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [draw]);

  const msAt = (clientX: number) => {
    const canvas = canvasRef.current!;
    const drag = dragRef.current!;
    const moved = drag.from + ((clientX - drag.x) / canvas.getBoundingClientRect().width) * windowMs;
    // A handle stays inside the file and leaves at least TRIM_MIN_MS between the two.
    if (drag.edge === 'start') return Math.round(Math.max(0, Math.min(heardTo - TRIM_MIN_MS, moved)));
    if (drag.edge === 'end') return Math.round(Math.min(durationMs, Math.max(heardFrom + TRIM_MIN_MS, moved)));
    return clamp(moved);
  };
  /** The handle under the pointer, when the lane is selected and trimming is allowed. */
  const handleAt = (clientX: number): TrimEdge | null => {
    if (!selected || !onTrim) return null;
    const rect = canvasRef.current!.getBoundingClientRect();
    const px = (fileMs: number) => rect.left + (Math.max(0, Math.min(windowMs, fileMs + offsetMs)) / windowMs) * rect.width;
    if (Math.abs(clientX - px(heardFrom)) <= HANDLE_HIT_PX) return 'start';
    if (Math.abs(clientX - px(heardTo)) <= HANDLE_HIT_PX) return 'end';
    return null;
  };

  /**
   * A drag ends at the release, wherever it happens: past the lane's edge it lands on the edge (positions are
   * clamped), and a release the lane never heard (capture lost) ends it at the last place it was seen.
   */
  const finish = (clientX: number) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    if (drag.edge) onTrim?.(drag.edge, msAt(clientX), true);
    else onNudge(msAt(clientX), true);
  };

  return (
    <canvas
      ref={canvasRef}
      className="sound-lane"
      role="slider"
      tabIndex={0}
      aria-label="Where the sound starts"
      aria-valuemin={clamp(-Infinity)}
      aria-valuemax={clamp(Infinity)}
      aria-valuenow={offsetMs}
      aria-valuetext={`${(offsetMs / 1000).toFixed(2)} s from the clip's first frame`}
      onPointerDown={(e) => {
        const edge = handleAt(e.clientX);
        if (!edge) onSelect?.();
        dragRef.current = { x: e.clientX, from: edge === 'start' ? heardFrom : edge === 'end' ? heardTo : offsetMs, edge };
        lastX.current = e.clientX;
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const drag = dragRef.current;
        // No button held: the release was lost (past the lane's edge, or the capture dropped); it ends here.
        if (drag && e.buttons === 0) return finish(lastX.current);
        if (drag) lastX.current = e.clientX;
        if (drag?.edge) onTrim?.(drag.edge, msAt(e.clientX), false);
        else if (drag) onNudge(msAt(e.clientX), false);
        else e.currentTarget.dataset['cursor'] = handleAt(e.clientX) ? 'trim' : 'default';
      }}
      onPointerUp={(e) => finish(e.clientX)}
      onPointerCancel={() => finish(lastX.current)}
      onLostPointerCapture={() => finish(lastX.current)}
      onKeyDown={(e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const step = KEY_STEP_MS * (e.shiftKey ? KEY_STEP_SHIFT : 1) * (e.key === 'ArrowLeft' ? -1 : 1);
        keyRef.current = clamp((keyRef.current ?? offsetMs) + step);
        onNudge(keyRef.current, false);
      }}
      onKeyUp={() => {
        if (keyRef.current === null) return;
        onNudge(keyRef.current, true);
        keyRef.current = null;
      }}
    />
  );
}
